///
/// EarthBrowser
///
/// Created by Matt Giger on 9/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export interface ImergFrameRecord extends DatasetFeature {
	id: string // slot_timestamp
	product: string
	data_timestamp: string
	slot_timestamp: string
	image_url: string
	source_path: string
	size_bytes: number
	width: number
	height: number
	vmin: number
	vmax: number
	bounds_wgs84: string | null
	created_at: string
}

const cBatchSize = 500

export class ImergTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists imerg_frames (
					id text primary key,
					product text not null,
					data_timestamp text not null,
					slot_timestamp text not null,
					image_url text not null,
					source_path text not null,
					size_bytes integer not null,
					width integer not null,
					height integer not null,
					vmin real not null,
					vmax real not null,
					bounds_wgs84 text,
					created_at text not null
				)`
			},
			{ sql: "create index if not exists idx_imerg_frames_data_timestamp on imerg_frames(data_timestamp desc)" },
			{ sql: "create index if not exists idx_imerg_frames_slot_timestamp on imerg_frames(slot_timestamp desc)" },
			{ sql: "create index if not exists idx_imerg_frames_product on imerg_frames(product)" },
		])
	}

	public static async replaceAll(rows: ImergFrameRecord[]): Promise<void> {
		await Database.db.exec("delete from imerg_frames", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: ImergFrameRecord[]): Promise<void> {
		const statement = `insert into imerg_frames (
				id, product, data_timestamp, slot_timestamp,
				image_url, source_path, size_bytes,
				width, height, vmin, vmax,
				bounds_wgs84, created_at
			) values (
				?, ?, ?, ?,
				?, ?, ?,
				?, ?, ?, ?,
				?, ?
			)
			on conflict(id) do update set
				product = excluded.product,
				data_timestamp = excluded.data_timestamp,
				slot_timestamp = excluded.slot_timestamp,
				image_url = excluded.image_url,
				source_path = excluded.source_path,
				size_bytes = excluded.size_bytes,
				width = excluded.width,
				height = excluded.height,
				vmin = excluded.vmin,
				vmax = excluded.vmax,
				bounds_wgs84 = excluded.bounds_wgs84,
				created_at = excluded.created_at`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(r => [
				r.id,
				r.product,
				r.data_timestamp,
				r.slot_timestamp,
				r.image_url,
				r.source_path,
				r.size_bytes,
				r.width,
				r.height,
				r.vmin,
				r.vmax,
				r.bounds_wgs84,
				r.created_at,
			]))
		}
	}

	// First-paint read for the precipitation layer; high priority so it
	// doesn't queue behind fresh-install bulk ingests (see RadarTable.getRecent)
	public static async getRecent(limit = 48): Promise<ImergFrameRecord[]> {
		const rows = await Database.db.exec(
			`select
				id, product, data_timestamp, slot_timestamp,
				image_url, source_path, size_bytes,
				width, height, vmin, vmax,
				bounds_wgs84, created_at
			from imerg_frames
			order by slot_timestamp desc
			limit ${Math.max(1, Math.min(limit, 5000))}`,
			[],
			"high"
		) as ImergFrameRecord[]
		return rows
	}
}
